// Drives the document theme from the UI store. Thin wrapper over the pure
// primitives in ./theme-core: on mount it resolves the initial theme
// (explicit stored > system preference > dark) and on every change it applies
// `data-theme` + `.dark` + `color-scheme` and persists the choice. The pre-paint
// inline script in index.html (generated from noFlashSnippet) prevents a flash;
// this provider re-applies idempotently after hydration.
import { useEffect, useLayoutEffect, useState, type ReactNode } from "react";
import {
  useUiStore,
  type Theme as ThemeType,
  type ThemePreference,
} from "@/lib/store/ui-store";
import {
  applyTheme,
  persistThemePreference,
  readStoredThemePreference,
  resolveTheme,
  systemPreference,
  SYSTEM_LIGHT_QUERY,
} from "./theme-core";

function currentStoredPreference(): ThemePreference {
  try {
    if (typeof localStorage === "undefined") return "system";
    return readStoredThemePreference(localStorage) ?? "system";
  } catch {
    return "system";
  }
}

function currentMediaQuery(): MediaQueryList | null {
  if (typeof window === "undefined") return null;
  if (typeof window.matchMedia !== "function") return null;
  return window.matchMedia(SYSTEM_LIGHT_QUERY);
}

function currentSystem(mql: MediaQueryList | null): ThemeType | null {
  try {
    return mql ? systemPreference(mql) : null;
  } catch {
    return null;
  }
}

function persistPreference(preference: ThemePreference): void {
  if (typeof localStorage === "undefined") return;
  persistThemePreference(localStorage, preference);
}

export function ThemeProvider({
  children,
}: {
  children: ReactNode;
}): React.JSX.Element {
  const theme = useUiStore((s) => s.theme);
  const preference = useUiStore((s) => s.themePreference);
  const setThemePreference = useUiStore((s) => s.setThemePreference);
  const [hydrated, setHydrated] = useState(false);

  // Hydrate store once: explicit stored choice > system preference > dark.
  useLayoutEffect(() => {
    const storedPreference = currentStoredPreference();
    const resolved = resolveTheme(
      storedPreference,
      currentSystem(currentMediaQuery()),
    );
    setThemePreference(storedPreference, resolved);
    applyTheme(document.documentElement, resolved);
    setHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mirror store → <html> (data-theme + .dark + color-scheme) and persist.
  useEffect(() => {
    if (!hydrated) return;
    applyTheme(document.documentElement, theme);
    persistPreference(preference);
  }, [hydrated, preference, theme]);

  // Operador 08/07/2026: preferência "system" resolve SEMPRE dark (default do
  // produto) — sem listener de prefers-color-scheme; só a escolha explícita
  // do usuário (light/dark) muda o tema.

  return <>{children}</>;
}
